import { motion } from "framer-motion";
import { ArrowRight, Globe, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { IMAGES } from "./site";

export const Hero = () => (
    <section id="top" className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-ground-soft/50 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 right-0 h-[28rem] w-[28rem] rounded-full bg-sun/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 pt-16 pb-20 lg:grid-cols-12 lg:pt-24 lg:pb-28">
            <motion.div
                className="lg:col-span-7"
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
            >
                <p className="inline-flex items-center gap-2 rounded-full border border-ground-line bg-ground-soft/60 px-4 py-1.5 text-xs font-semibold tracking-wide text-sun uppercase" data-testid="hero-eyebrow">
                    <Sparkles className="h-3.5 w-3.5 text-sun" />
                    Better English. Brighter Future.
                </p>
                <h1 className="font-heading mt-6 text-4xl font-black tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
                    Online English Tutoring for Children in{" "}
                    <span className="text-sun italic">Grades 1–6</span>
                </h1>
                <p className="mt-6 max-w-xl text-base text-mist sm:text-lg" data-testid="hero-subheadline">
                    Helping young learners become confident readers, stronger writers and more capable communicators
                    through supportive online English lessons.
                </p>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                    <Button asChild size="lg" className="rounded-full px-7 text-base font-bold shadow-xl shadow-sun/25 transition-transform duration-200 hover:scale-105">
                        <a href="#lessons" data-testid="hero-explore-lessons-btn">
                            Explore Our Lessons
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </a>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="rounded-full border-sun/50 bg-transparent px-7 text-base font-semibold text-sun transition-colors duration-200 hover:bg-sun/10 hover:text-sun">
                        <a href="#contact" data-testid="hero-send-enquiry-btn">Send an Enquiry</a>
                    </Button>
                </div>
                <p className="mt-7 flex items-center gap-2 text-sm text-mist" data-testid="hero-countries-note">
                    <Globe className="h-4 w-4 shrink-0 text-sun" />
                    Supporting families in South Africa, the United Kingdom and the United States.
                </p>
            </motion.div>
            <motion.div
                className="relative lg:col-span-5"
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
            >
                <div className="absolute -inset-3 rounded-[2rem] border-2 border-sun/40" />
                <img
                    src={IMAGES.hero}
                    alt="Young learner with headphones taking part in an online English lesson"
                    data-testid="hero-image"
                    className="relative aspect-[5/4] w-full rounded-[2rem] object-cover shadow-2xl shadow-black/40"
                    loading="eager"
                />
                <div className="animate-float-slow absolute -bottom-6 -left-4 rounded-2xl border border-ground-line bg-ground px-5 py-4 shadow-xl shadow-black/40 sm:-left-8" data-testid="hero-badge">
                    <p className="font-heading text-2xl font-black text-sun">Grades 1–6</p>
                    <p className="text-xs font-medium text-mist">Live online lessons from home</p>
                </div>
            </motion.div>
        </div>
    </section>
);
